/**
 * Game Builder API - backend app/game_builder/routers/game_builder.py, docs/HOW_IT_WORKS.md. Plans are the org's (the login token says which).
 * Slow steps (build a mission, build a game, draft a plan) run as background jobs on the server:
 * they answer {status: 'queued', job} and `untilDone` polls GET /jobs/<id> until there's a result.
 * When the result is already saved they answer {status: 'done', result} straight away.
 */
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL;
const BASE = `${API_BASE}/api/game-builder`;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const json = () => ({ headers: { ...authHeaders(), 'Content-Type': 'application/json' }, timeout: 60000 });

const messageOf = (error) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (error.response?.status === 401) return 'Your session has ended. Log in again.';
  if (error.response?.status === 403) return "You don't have access to that.";
  if (error.response?.status === 429) return error.response.data?.detail || 'Too many requests. Try again in a moment.';
  if (error.code === 'ECONNABORTED') return 'The server took too long to answer. Try again.';
  if (!error.response) return "Can't reach the server. Check your connection and try again.";
  return 'Something went wrong. Try again in a moment.';
};

const call = async (request) => {
  try {
    return (await request()).data;
  } catch (error) {
    throw new Error(messageOf(error));
  }
};

const POLL_MS = [1000, 2000, 3000];       // then every 3s
const JOB_LIMIT_MS = 12 * 60 * 1000;      // the server gives a job 10 minutes

/** Resolves with a slow step's result, polling its job if it's still running. */
export async function untilDone(started, onProgress) {
  let job = await started;
  const begun = Date.now();
  for (let n = 0; job.status === 'queued' || job.status === 'running'; n += 1) {
    if (Date.now() - begun > JOB_LIMIT_MS) throw new Error('This is taking much longer than usual. Try again.');
    onProgress?.(job.status);
    await new Promise((r) => setTimeout(r, POLL_MS[Math.min(n, POLL_MS.length - 1)]));
    const jobId = job.job;
    job = await call(() => axios.get(`${BASE}/jobs/${jobId}`, json()));
  }
  if (job.status === 'error') throw new Error(job.error || 'Something went wrong. Try again in a moment.');
  return job.result;
}

// ---- plans
export const listPlans = () => call(() => axios.get(`${BASE}/plans`, json()));
export const getPlan = (planId) => call(() => axios.get(`${BASE}/plans/${encodeURIComponent(planId)}`, json()));
export const deletePlan = (planId) => call(() => axios.delete(`${BASE}/plans/${encodeURIComponent(planId)}`, json()));
/** A copy of the plan to change - its missions' designs and scene picks come along, its share links don't. */
export const copyPlan = (planId) => call(() => axios.post(`${BASE}/plans/${encodeURIComponent(planId)}/copy`, null, json()));

// ---- one mission (slow steps: wrap in untilDone)
const missionUrl = (planId, missionId) => `${BASE}/plans/${encodeURIComponent(planId)}/missions/${encodeURIComponent(missionId)}`;
/** `fresh`: throw away the saved mission (and its game) and build it again - one AI call, new art later. */
export const buildMission = (planId, missionId, onProgress, fresh = false) =>
  untilDone(call(() => axios.post(`${missionUrl(planId, missionId)}/build`, fresh ? { fresh: true } : null, json())), onProgress);
/** The winning path (AI call 1) - saved, so asking again is instant; `fresh` throws the mission away and asks afresh. */
export const makePath = (planId, missionId, onProgress, fresh = false) =>
  untilDone(call(() => axios.post(`${missionUrl(planId, missionId)}/path`, fresh ? { fresh: true } : null, json())), onProgress);
export const getPath = (planId, missionId) => call(() => axios.get(`${missionUrl(planId, missionId)}/path`, json()));
/** The creator's edited path: saved and checked (no AI) - its problems come back. */
export const savePath = (planId, missionId, path) => call(() => axios.put(`${missionUrl(planId, missionId)}/path`, { path }, json()));
/** Build the mission from the approved path: the words (AI call 2), then code. Answers like buildMission. */
export const approvePath = (planId, missionId, onProgress) =>
  untilDone(call(() => axios.post(`${missionUrl(planId, missionId)}/path/approve`, null, json())), onProgress);
/** `redo`: e.g. ['background'] - a fresh background with today's prompt (one image call). */
export const buildGame = (planId, missionId, onProgress, redo = []) =>
  untilDone(call(() => axios.post(`${missionUrl(planId, missionId)}/game`, redo.length ? { redo } : null, json())), onProgress);
/** `art`: the creator's picture choices {item id: picture number} (omit to leave them as they are). */
export const saveLayout = (planId, missionId, positions, art) =>
  call(() => axios.put(`${missionUrl(planId, missionId)}/layout`, art ? { positions, art } : { positions }, json()));

// ---- the item inventory (catalog page)
export const getInventory = () => call(() => axios.get(`${BASE}/inventory`, json()));
/** Several items in one scene, each working on its own - the catalog's "Try together". */
export const tryTogether = (items) => call(() => axios.post(`${BASE}/inventory/try`, { items }, json()));

// ---- the creator's corrections
/** {published: null | {version, token, published_at}} - guests play it at /play/<token>. */
export const getPublished = (planId, missionId) => call(() => axios.get(`${missionUrl(planId, missionId)}/publish`, json()));
/** Freeze the game as it is now for guests (a new version; the link stays the same). */
export const publishGame = (planId, missionId) => call(() => axios.post(`${missionUrl(planId, missionId)}/publish`, {}, json()));
/** The guest link stops working (the plays stay in the report). */
export const unpublishGame = (planId, missionId) => call(() => axios.delete(`${missionUrl(planId, missionId)}/publish`, json()));
/** How the guests did: attempts, finished, the usual time, the most common mistakes. */
export const getReport = (planId, missionId) => call(() => axios.get(`${missionUrl(planId, missionId)}/report`, json()));
/** Draw one item again (one image call), in the creator's words if given. Resolves with the game spec. */
export const redrawItem = (planId, missionId, itemId, looks, onProgress) =>
  untilDone(call(() => axios.post(`${missionUrl(planId, missionId)}/items/${encodeURIComponent(itemId)}/redraw`, { looks }, json())), onProgress);

// ---- the shared picture library (backend library.py): a room's backgrounds, an item's pictures - choosing one is free
export const getBackgrounds = (planId, missionId) => call(() => axios.get(`${missionUrl(planId, missionId)}/backgrounds`, json()));
/** Use another background of the room. Resolves with the game spec. */
export const chooseBackground = (planId, missionId, variant) =>
  call(() => axios.put(`${missionUrl(planId, missionId)}/background`, { variant }, json()));
export const getItemPictures = (planId, missionId, itemId) =>
  call(() => axios.get(`${missionUrl(planId, missionId)}/items/${encodeURIComponent(itemId)}/pictures`, json()));
/** Use another picture of the item (its own, or one a creator had drawn). Resolves with the game spec. */
export const chooseItemPicture = (planId, missionId, itemId, ref, state = null) =>
  call(() => axios.put(`${missionUrl(planId, missionId)}/items/${encodeURIComponent(itemId)}/picture`, state ? { ref, state } : { ref }, json()));

/** Two items' pictures the wrong way round: each takes the other's (no image call). Resolves with the game spec. */
export const swapItemPictures = (planId, missionId, itemId, other) =>
  call(() => axios.put(`${missionUrl(planId, missionId)}/items/${encodeURIComponent(itemId)}/picture/swap`, { other }, json()));

/** Draw pictures for several items at once (ONE image call: a sheet, cut apart; they join the shared library). Resolves with the game spec. */
export const drawItems = (planId, missionId, items, onProgress) =>
  untilDone(call(() => axios.post(`${missionUrl(planId, missionId)}/pictures/draw`, { items }, json())), onProgress);

// ---- new plans: source -> chat -> draft -> save
export const uploadSource = (file) => {
  const form = new FormData();
  form.append('file', file);
  return call(() => axios.post(`${BASE}/sources/upload`, form, { headers: authHeaders(), timeout: 60000 }));
};
export const fetchSource = (url) => call(() => axios.post(`${BASE}/sources/url`, { url }, json()));
export const planChat = (document, messages) => call(() => axios.post(`${BASE}/plan-chat`, { document, messages }, json()));
export const draftPlan = (document, messages, onProgress) =>
  untilDone(call(() => axios.post(`${BASE}/plan-drafts`, { document, messages }, json())), onProgress);
export const savePlan = (plan) => call(() => axios.post(`${BASE}/plans`, { plan }, json()));

// ---- storyline: missions linked by a time skip or a move to another room
export const suggestLinks = (planId, missions) =>
  call(() => axios.post(`${BASE}/plans/${encodeURIComponent(planId)}/storyline/suggest`, { missions }, json()));
export const saveStoryline = (planId, missions, links) =>
  call(() => axios.put(`${BASE}/plans/${encodeURIComponent(planId)}/storyline`, { missions, links }, json()));
export const deleteStoryline = (planId) =>
  call(() => axios.delete(`${BASE}/plans/${encodeURIComponent(planId)}/storyline`, json()));

// ---- site map: the rooms where missions happen (the player's map view)
export const suggestSite = (planId) =>
  call(() => axios.post(`${BASE}/plans/${encodeURIComponent(planId)}/site/suggest`, null, json()));
export const saveSite = (planId, site) =>
  call(() => axios.put(`${BASE}/plans/${encodeURIComponent(planId)}/site`, { site }, json()));
export const deleteSite = (planId) =>
  call(() => axios.delete(`${BASE}/plans/${encodeURIComponent(planId)}/site`, json()));
